class PredictiveSearch extends SearchForm {
  constructor() {
    super();
    this.cachedResults = {};
    this.predictiveSearchResults = this.querySelector('[data-predictive-search]');
    this.allPredictiveSearchInstances = document.querySelectorAll('predictive-search');
    this.isOpen = false;
    this.abortController = new AbortController();
    this.searchTerm = '';
    this.setupEventListeners();
  }
  setupEventListeners() {
    this.input.form.addEventListener('submit', this.onFormSubmit.bind(this));
    this.input.addEventListener('focus', this.onFocus.bind(this));
    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.addEventListener('keyup', this.onKeyup.bind(this));
    this.addEventListener('keydown', this.onKeydown.bind(this));
    this.setupDismissListeners();
  }
  /*
    Dismiss on an outside click, and on Escape.

    ⚠ ONLY FOR INSTANCES OUTSIDE `.header__search`, and that guard is the whole
    point. The masthead's search lives in a `<details-modal>` with its own
    overlay: clicking away already closes the entire modal, and a second
    closer here would fight DetailsModal for the same gesture.

    The slim bar's instance (2026-08-27) and the /search template's have no
    modal and no overlay, so before this they had no way to be dismissed at
    all — `onFocusOut` deliberately returns early while a term is in the box,
    which is correct for a panel you can click around inside, but it left the
    results stuck open until the term was cleared. That is what Jordan hit.

    ⚠ `click`, NOT `pointerdown`. A result row is a link: closing the panel on
    pointerdown tears the element out from under the pointer before the click
    completes, and the navigation is lost. On `click` the target is still in
    the tree, `this.contains()` is true, and we leave it alone.

    ⚠ Capture phase, because we are only OBSERVING. The cart-drawer app patches
    document-level handlers on this store, and a bubble-phase listener can be
    cut off by anything upstream calling stopPropagation. Nothing is stopped or
    prevented here, so running early is free.

    `close()` and not `close(true)`: the term stays in the box, so clicking
    back into the field reopens the results rather than starting from nothing.
  */
  setupDismissListeners() {
    if (this.closest('.header__search')) return;

    this._onDocumentClick = (event) => {
      if (!this.isOpen) return;
      if (this.contains(event.target)) return;
      this.close();
    };
    this._onDocumentKeydown = (event) => {
      if (event.key !== 'Escape' || !this.isOpen) return;
      this.close();
      this.input.blur();
    };

    document.addEventListener('click', this._onDocumentClick, true);
    document.addEventListener('keydown', this._onDocumentKeydown, true);
  }
  disconnectedCallback() {
    if (this._onDocumentClick) document.removeEventListener('click', this._onDocumentClick, true);
    if (this._onDocumentKeydown) document.removeEventListener('keydown', this._onDocumentKeydown, true);
    this.unbindViewportWatch();
  }
  getQuery() {
    return this.input.value.trim();
  }
  onChange() {
    super.onChange();
    const newSearchTerm = this.getQuery();
    if (!this.searchTerm || !newSearchTerm.startsWith(this.searchTerm)) {
      this.querySelector('#predictive-search-results-groups-wrapper')?.remove();
    }
    this.updateSearchForTerm(this.searchTerm, newSearchTerm);
    this.searchTerm = newSearchTerm;
    if (!this.searchTerm.length) {
      this.close(true);
      return;
    }
    this.getSearchResults(this.searchTerm);
  }
  onFormSubmit(event) {
    if (!this.getQuery().length || this.querySelector('[aria-selected="true"] a')) event.preventDefault();
  }
  onFormReset(event) {
    super.onFormReset(event);
    if (super.shouldResetForm()) {
      this.searchTerm = '';
      this.abortController.abort();
      this.abortController = new AbortController();
      this.closeResults(true);
    }
  }
  onFocus() {
    const currentSearchTerm = this.getQuery();
    if (!currentSearchTerm.length) return;
    if (this.searchTerm !== currentSearchTerm) {
      this.onChange();
    } else if (this.getAttribute('results') === 'true') {
      this.open();
    } else {
      this.getSearchResults(this.searchTerm);
    }
  }
  onFocusOut() {
    setTimeout(() => {
      // Keep results open while a term is in the box, even if focus leaves the
      // input (e.g. clicking empty space inside the panel). Clicking the overlay
      // still closes the whole modal via DetailsModal.
      if (this.getQuery().length) return;
      if (!this.contains(document.activeElement)) this.close();
    });
  }
  onKeyup(event) {
    if (!this.getQuery().length) this.close(true);
    event.preventDefault();
    switch (event.code) {
      case 'ArrowUp':
        this.switchOption('up');
        break;
      case 'ArrowDown':
        this.switchOption('down');
        break;
      case 'Enter':
        this.selectOption();
        break;
    }
  }
  onKeydown(event) {
    if (event.code === 'ArrowUp' || event.code === 'ArrowDown') {
      event.preventDefault();
    }
  }
  updateSearchForTerm(previousTerm, newTerm) {
    const searchForTextElement = this.querySelector('[data-predictive-search-search-for-text]');
    const currentButtonText = searchForTextElement?.innerText;
    if (currentButtonText) {
      if (currentButtonText.match(new RegExp(previousTerm, 'g')).length > 1) {
        return;
      }
      const newButtonText = currentButtonText.replace(previousTerm, newTerm);
      searchForTextElement.innerText = newButtonText;
    }
  }
  switchOption(direction) {
    if (!this.getAttribute('open')) return;
    const moveUp = direction === 'up';
    const selectedElement = this.querySelector('[aria-selected="true"]');
    const allVisibleElements = Array.from(this.querySelectorAll('li, button.predictive-search__item')).filter(
      (element) => element.offsetParent !== null && !element.closest('.ps-panel:not(.is-active)')
    );
    let activeElementIndex = 0;
    if (moveUp && !selectedElement) return;
    let selectedElementIndex = -1;
    let i = 0;
    while (selectedElementIndex === -1 && i <= allVisibleElements.length) {
      if (allVisibleElements[i] === selectedElement) {
        selectedElementIndex = i;
      }
      i++;
    }
    this.statusElement.textContent = '';
    if (!moveUp && selectedElement) {
      activeElementIndex = selectedElementIndex === allVisibleElements.length - 1 ? 0 : selectedElementIndex + 1;
    } else if (moveUp) {
      activeElementIndex = selectedElementIndex === 0 ? allVisibleElements.length - 1 : selectedElementIndex - 1;
    }
    if (activeElementIndex === selectedElementIndex) return;
    const activeElement = allVisibleElements[activeElementIndex];
    activeElement.setAttribute('aria-selected', true);
    if (selectedElement) selectedElement.setAttribute('aria-selected', false);
    this.input.setAttribute('aria-activedescendant', activeElement.id);
  }
  selectOption() {
    const selectedOption = this.querySelector('[aria-selected="true"] a, button[aria-selected="true"]');
    if (selectedOption) selectedOption.click();
  }
  getSearchResults(searchTerm) {
    const queryKey = searchTerm.replace(' ', '-').toLowerCase();
    this.setLiveRegionLoadingState();
    if (this.cachedResults[queryKey]) {
      this.renderSearchResults(this.cachedResults[queryKey]);
      return;
    }
    fetch(`${routes.predictive_search_url}?q=${encodeURIComponent(searchTerm)}&section_id=predictive-search`, {
      signal: this.abortController.signal,
    })
      .then((response) => {
        if (!response.ok) {
          var error = new Error(response.status);
          this.close();
          throw error;
        }
        return response.text();
      })
      .then((text) => {
        const resultsMarkup = new DOMParser()
          .parseFromString(text, 'text/html')
          .querySelector('#shopify-section-predictive-search').innerHTML;
        this.allPredictiveSearchInstances.forEach((predictiveSearchInstance) => {
          predictiveSearchInstance.cachedResults[queryKey] = resultsMarkup;
        });
        this.renderSearchResults(resultsMarkup);
      })
      .catch((error) => {
        if (error?.code === 20) {
          return;
        }
        this.close();
        throw error;
      });
  }
  setLiveRegionLoadingState() {
    this.statusElement = this.statusElement || this.querySelector('.predictive-search-status');
    this.loadingText = this.loadingText || this.getAttribute('data-loading-text');
    this.setLiveRegionText(this.loadingText);
    this.setAttribute('loading', true);
  }
  setLiveRegionText(statusText) {
    this.statusElement.setAttribute('aria-hidden', 'false');
    this.statusElement.textContent = statusText;
    setTimeout(() => {
      this.statusElement.setAttribute('aria-hidden', 'true');
    }, 1000);
  }
  renderSearchResults(resultsMarkup) {
    this.predictiveSearchResults.innerHTML = resultsMarkup;
    this.syncSearchTabs();
    this.setAttribute('results', true);
    this.setLiveRegionResults();
    this.open();
  }
  setLiveRegionResults() {
    this.removeAttribute('loading');
    this.setLiveRegionText(this.querySelector('[data-predictive-search-live-region-count-value]').textContent);
  }
  /*
    Measure from the PANEL's own top edge, not from the bottom of the header.

    The old version took `window.innerHeight - header.bottom`, which is the
    space under the header - but on /search the dropdown starts well below the
    header, under the page title and the field, so that number was always too
    large by exactly that distance and the last results hung off the bottom of
    the screen with no way to reach them.

    It also has to run AFTER the panel is displayed. Measuring a hidden element
    returns a zero rect, which made the cap the full viewport height.
  */
  getResultsMaxHeight() {
    const rect = this.predictiveSearchResults.getBoundingClientRect();
    /*
      On a phone the panel is a full-width sheet (mp-site.css §13c) and the strip
      of page still showing under it is the dismiss target, so 24px of clearance
      is not a tap target. Stop the sheet well clear of the bottom there.
    */
    const breathingRoom = window.matchMedia('(max-width: 989px)').matches ? 72 : 24;
    /*
      ⚠ `innerHeight` does NOT shrink for the on-screen keyboard on iOS - the
      layout viewport is unchanged and only the visual one moves - so a cap taken
      from it hides the last results behind the keyboard, on exactly the devices
      where this panel is hardest to reach. `visualViewport` reports what is
      actually on screen, and adding `offsetTop` puts its bottom edge back into
      the layout-viewport coordinates `rect.top` is measured in.
    */
    const viewport = window.visualViewport;
    const viewportBottom = viewport ? viewport.offsetTop + viewport.height : window.innerHeight;
    // A floor, so a panel opened near the bottom of a short window is still
    // usable rather than being squeezed to a sliver.
    this.resultsMaxHeight = Math.max(220, viewportBottom - rect.top - breathingRoom);
    return this.resultsMaxHeight;
  }
  /* The cap is relative to the viewport, so it goes stale the moment the page
     scrolls underneath an open panel. rAF-throttled, and only bound while
     something is actually open. */
  bindViewportWatch() {
    if (this._viewportWatch) return;
    let frame = null;
    this._viewportWatch = () => {
      if (frame) return;
      frame = requestAnimationFrame(() => {
        frame = null;
        if (!this.hasAttribute('open')) return;
        this.resultsMaxHeight = false;
        this.predictiveSearchResults.style.maxHeight = `${this.getResultsMaxHeight()}px`;
      });
    };
    window.addEventListener('scroll', this._viewportWatch, { passive: true });
    window.addEventListener('resize', this._viewportWatch);
    // The keyboard opening is a visualViewport resize and nothing else on iOS:
    // no window resize, no window scroll. Without these two the cap above is
    // computed once, before the keyboard exists, and never corrected.
    window.visualViewport?.addEventListener('resize', this._viewportWatch);
    window.visualViewport?.addEventListener('scroll', this._viewportWatch);
  }
  unbindViewportWatch() {
    if (!this._viewportWatch) return;
    window.removeEventListener('scroll', this._viewportWatch);
    window.removeEventListener('resize', this._viewportWatch);
    window.visualViewport?.removeEventListener('resize', this._viewportWatch);
    window.visualViewport?.removeEventListener('scroll', this._viewportWatch);
    this._viewportWatch = null;
  }
  open() {
    // In the restyled header modal the whole rounded panel scrolls, so don't cap the
    // results height here (that would create a nested scroll area). Search-page template keeps the cap.
    const headerSearch = this.closest('.header__search');
    if (!headerSearch) {
      // `open` first: the panel is display:none until it is set, and a hidden
      // element measures as a zero rect.
      this.setAttribute('open', true);
      this.resultsMaxHeight = false;
      this.predictiveSearchResults.style.maxHeight = `${this.getResultsMaxHeight()}px`;
      this.bindViewportWatch();
    } else {
      // Mobile: expand the panel from its collapsed (field-only) height now that
      // results exist. CSS transitions max-height on .ps-has-results (desktop ignores it).
      headerSearch.classList.add('ps-has-results');
    }
    this.setAttribute('open', true);
    this.input.setAttribute('aria-expanded', true);
    this.isOpen = true;
  }
  close(clearSearchTerm = false) {
    this.closeResults(clearSearchTerm);
    this.isOpen = false;
  }
  closeResults(clearSearchTerm = false) {
    if (clearSearchTerm) {
      this.input.value = '';
      this.removeAttribute('results');
    }
    const selected = this.querySelector('[aria-selected="true"]');
    if (selected) selected.setAttribute('aria-selected', false);
    this.input.setAttribute('aria-activedescendant', '');
    this.removeAttribute('loading');
    this.removeAttribute('open');
    this.input.setAttribute('aria-expanded', false);
    this.resultsMaxHeight = false;
    this.predictiveSearchResults.removeAttribute('style');
    this.unbindViewportWatch();
    // Collapse the mobile panel back to the field-only height when results clear.
    this.closest('.header__search')?.classList.remove('ps-has-results');
  }
  // --- Tabbed results (Products / Suggestions / Collections / Pages) ---
  syncSearchTabs() {
    const shell = this.predictiveSearchResults.querySelector('.ps-shell');
    if (!shell) return;
    const panels = Array.from(shell.querySelectorAll('.ps-panel'));
    if (!panels.length) return;
    const keys = panels.map((panel) => panel.dataset.psPanel);
    // Keep the previously active tab if it still has results, else default to Products, else first tab.
    let active = this._activeTab && keys.includes(this._activeTab) ? this._activeTab : null;
    if (!active) active = keys.includes('products') ? 'products' : keys[0];
    this.applySearchTab(active);
    shell.querySelectorAll('.ps-tab').forEach((tab) => {
      tab.addEventListener('click', (event) => {
        event.preventDefault();
        this.applySearchTab(tab.dataset.psTab);
      });
    });
  }
  applySearchTab(key) {
    const shell = this.predictiveSearchResults.querySelector('.ps-shell');
    if (!shell) return;
    this._activeTab = key;
    shell.querySelectorAll('.ps-tab').forEach((tab) => {
      tab.classList.toggle('is-active', tab.dataset.psTab === key);
    });
    shell.querySelectorAll('.ps-panel').forEach((panel) => {
      panel.classList.toggle('is-active', panel.dataset.psPanel === key);
    });
  }
}

customElements.define('predictive-search', PredictiveSearch);
